import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaUser, FaEnvelope, FaPhone, FaCommentAlt, FaPaperPlane, FaLock } from 'react-icons/fa';
import ReCAPTCHA from 'react-google-recaptcha';
import './contactform_main.css';
import emailjs from '@emailjs/browser';

const EMAIL_CONFIG = {
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "KJva8aTlfsI0P1wss",
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_xtksca9",
  userTemplateId: process.env.REACT_APP_EMAILJS_USER_TEMPLATE_ID || "template_l1omtzk",
  adminTemplateId: process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID || "template_gs3p8tk",
  adminEmail: process.env.REACT_APP_ADMIN_EMAIL || "contact@dgfuturetech.com",
  replyToEmail: process.env.REACT_APP_REPLY_TO_EMAIL || "contact@dgfuturetech.com"
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6Ld4_JktAAAAAEjyhW0mTFD_T__ZTeAYV_z8afAH";

emailjs.init(EMAIL_CONFIG.publicKey);

const ContactForm = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    if (!formData.companyName.trim()) {
      formErrors.companyName = '御社名をご記入ください';
      isValid = false;
    }

    if (!formData.firstName.trim()) {
      formErrors.firstName = '姓をご記入ください';
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      formErrors.lastName = '名をご記入ください';
      isValid = false;
    }

    if (!formData.email.trim()) {
      formErrors.email = 'メールアドレスをご記入ください';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      formErrors.email = 'メールアドレスをご記入ください';
      isValid = false;
    }

    if (!formData.phone.trim()) {
      formErrors.phone = '電話番号をご記入ください';
      isValid = false;
    }

    if (!formData.message.trim()) {
      formErrors.message = 'お問合せ内容をご記入ください';
      isValid = false;
    }

    if (!captchaToken) {
      isValid = false;
      alert("チェックボックスにチェックを入れてください");
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const sendEmails = async () => {
    try {
      const customerFullName = `${formData.firstName} ${formData.lastName}`;
      const company = `${formData.companyName} `;
      const contactConfirmationMessage =
`${company} 
${customerFullName} 様 

この度は、DGフューチャーテックインディアへお問い合わせいただき、誠にありがとうございます。

お問い合わせを確かに受け付けいたしました。
いただいた内容を確認のうえ、担当者より改めてご連絡申し上げます。

恐れ入りますが、今しばらくお待ちくださいますよう、お願い申し上げます。

※本メールは、お問い合わせを受け付けた際に自動送信されています。
本メールへのご返信はお控えくださいますようお願いいたします。

――――――――――
DGフューチャーテックインディア`;

      const userTemplateParams = {
        email_subject: "お問い合わせいただきありがとうございます。",
        to_name: customerFullName,
        to_email: formData.email,
        company: formData.companyName,
        company_display: formData.companyName ? 'block' : 'none',
        contact_confirmation_message: contactConfirmationMessage,
        assessment_section_visible: 'none',
        contact_section_visible: 'block',
        user_score: "",
        questions_total: "",
        percentage: "",
        assessment_outcome_message: "",
        reply_to: EMAIL_CONFIG.replyToEmail,
      };

      console.log("Sending contact confirmation to user:", formData.email);
      const userResponse = await emailjs.send(
        EMAIL_CONFIG.serviceId,
        EMAIL_CONFIG.userTemplateId,
        userTemplateParams
      );
      console.log("User contact confirmation sent successfully:", userResponse.status);

      const adminTemplateParams = {
        header_text: "New Contact Form Submission",
        admin_name: "Admin",
        to_email: EMAIL_CONFIG.adminEmail,
        notification_message: `A new message has been received via the website contact form from ${formData.firstName} ${formData.lastName}.`,
        user_name: `${formData.firstName} ${formData.lastName}`,
        user_email: formData.email,
        company: formData.companyName || "Not provided",
        company_display: formData.companyName ? 'block' : 'none',
        submitted_message: formData.message,
        user_phone: formData.phone || "Not provided",
        assessment_details_visible: 'none',
        contact_details_visible: 'block',

        user_score: "",
        questions_total: "",
        percentage: "",
        reply_to: formData.email
      };

      console.log("Sending contact notification to admin:", EMAIL_CONFIG.adminEmail);
      const adminResponse = await emailjs.send(
        EMAIL_CONFIG.serviceId,
        EMAIL_CONFIG.adminTemplateId,
        adminTemplateParams
      );
      console.log("Admin contact notification sent successfully:", adminResponse.status);

      return true;
    } catch (error) {
      console.error('Error sending contact emails:', error.text || error.message || error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        const success = await sendEmails();

        if (success) {
          setShowToast(true);
          setTimeout(() => setShowToast(false), 5000);

          setFormData({
            companyName: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            message: ''
          });
          setCaptchaToken(null);
          recaptchaRef.current?.reset();
        } else {
          alert("There was a problem sending your message. Please try again later.");
          setCaptchaToken(null);
          recaptchaRef.current?.reset();
        }
      } catch (error) {
        console.error("Form submission error:", error);
        alert("There was a problem sending your message. Please try again later.");
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="form-section">
      <Link to="/jp" className="page-close-btn">
        ╳
      </Link>

      {showToast && (
        <div className="custom-toast">
          <FaCheckCircle className="toast-icon" />
          <div className="toast-content">
            <h4>お問合せを受け付けました</h4>
            <p>内容を確認の上、追って担当者よりご連絡させて頂きます</p>
          </div>
          <button className="toast-close" onClick={() => setShowToast(false)}>×</button>
        </div>
      )}

      <div className="form-header">
        <div className="form-header-icon">
          <FaEnvelope />
        </div>
        <h2>お客様情報</h2>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label htmlFor="companyName">御社名</label>
          <input
            id="companyName"
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            maxLength={100}
            className={errors.companyName ? 'field-invalid' : ''}
            placeholder={errors.companyName || "株式会社サンプル"}
            required
          />
        </div>

        <div className="field-row">
          <div className="field">
            <label htmlFor="firstName">姓</label>
            <input
              id="firstName"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              maxLength={100}
              className={errors.firstName ? 'field-invalid' : ''}
              placeholder={errors.firstName || "山田"}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="lastName">名</label>
            <input
              id="lastName"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              maxLength={100}
              className={errors.lastName ? 'field-invalid' : ''}
              placeholder={errors.lastName || "一郎"}
              required
            />
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label htmlFor="email">メールアドレス</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              maxLength={100}
              className={errors.email ? 'field-invalid' : ''}
              placeholder={errors.email || "contact@dgfuturetech.com"}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="phone">電話</label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              maxLength={20}
              className={errors.phone ? 'field-invalid' : ''}
              placeholder={errors.phone || "090-1234-56XX"}
              required
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="message">お問合せ内容</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            maxLength={1500}
            className={errors.message ? 'field-invalid' : ''}
            placeholder={errors.message || "お問合せ内容をご記入ください"}
            required
          />
          <div className="char-count">
            {formData.message.length}/1500
          </div>
        </div>

        <div className="field captcha-group">
          <div className="recaptcha-wrapper">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
            />
          </div>
        </div>

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          <FaPaperPlane className="btn-icon" />
          {isSubmitting ? '送信中...' : '送信'}
        </button>

        <p className="form-footer-note">
          <FaLock className="lock-icon" />
          ご入力いただいた情報は安全に送信されます。
        </p>
      </form>
    </div>
  );
};

export default ContactForm;